import { DisposableContext } from "@bodil/core/disposable";
import { Component } from "@bodil/dom/component";
import { animationFrame, prefersReducedMotion } from "@bodil/dom/dom";
import { eventListener } from "@bodil/dom/event";
import type { ReactiveController, ReactiveControllerHost } from "lit";

import styles from "./ripple.css?inline";

/**
 * Play a ripple animation on mousedown.
 *
 * The target element needs to have the
 * following CSS properties defined:
 *
 * @example
 * position: relative;
 * overflow: hidden;
 */
export class RippleController implements ReactiveController {
    private readonly rippleContext = new DisposableContext();

    constructor(private readonly host: ReactiveControllerHost) {
        this.host.addController(this);
        if (this.host instanceof Component) {
            this.host.addStyleSheet(styles);
        }
    }

    hostDisconnected() {
        this.rippleContext.dispose();
    }

    playRipple(target: HTMLElement, event: MouseEvent) {
        if (prefersReducedMotion()) {
            return;
        }
        this.rippleContext.dispose();
        this.rippleContext.use(
            animationFrame(() => {
                const rect = target.getBoundingClientRect();
                const radius = Math.max(rect.width, rect.height) / 2;
                const ripple = document.createElement("span");
                ripple.classList.add("ripple");
                ripple.style.width = ripple.style.height = `${radius * 2}px`;
                ripple.style.left = `${event.clientX - (rect.left + radius)}px`;
                ripple.style.top = `${event.clientY - (rect.top + radius)}px`;
                const root = target.shadowRoot ?? target;
                root.append(ripple);

                const cancel = () => this.rippleContext.dispose();
                for (const anim of ripple.getAnimations()) {
                    this.rippleContext.use(eventListener(anim, "cancel", cancel, { once: true }));
                    this.rippleContext.use(eventListener(anim, "finish", cancel, { once: true }));
                }
                this.rippleContext.use(() => ripple.remove());
            }),
        );
    }
}
